import {
  Alert,
  Button,
  Checkbox,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { FC } from 'react';

import {
  type AssignmentMapCapabilities,
  isTeamAssignmentVariant,
} from '../../lib/assignment-context';
import {
  PreviewPolygonResponse,
  SamplingPeriod,
} from '../../data-access/types/gp-sampling-assignment';

import { buildPeriodSelectOptions, type DrawActionMode } from './assignment-map-constants';

interface SamplerOrganizationFieldsProps {
  disabled?: boolean;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  onOrganizationIdChange: (value: string | null) => void;
  onSamplerIdChange: (value: string | null) => void;
  organizationId: string | null;
  organizationOptions: { value: string; label: string }[];
  samplerId: string | null;
  samplerOptions: { value: string; label: string }[];
}

const SamplerOrganizationFields: FC<SamplerOrganizationFieldsProps> = ({
  disabled = false,
  i18n,
  onOrganizationIdChange,
  onSamplerIdChange,
  organizationId,
  organizationOptions,
  samplerId,
  samplerOptions,
}) => (
  <Stack gap="xs">
    <Select
      clearable
      data={organizationOptions}
      disabled={disabled}
      label={i18n('selectSamplerOrganization')}
      onChange={(value) => {
        onOrganizationIdChange(value);
        onSamplerIdChange(null);
      }}
      searchable
      size="sm"
      value={organizationId}
    />
    <Select
      data={samplerOptions}
      disabled={disabled || !organizationId}
      label={i18n('selectSampler')}
      onChange={onSamplerIdChange}
      searchable
      size="sm"
      value={samplerId}
    />
  </Stack>
);

interface PeriodSamplerFieldsProps {
  disabled?: boolean;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  onPeriodChange: (period: SamplingPeriod) => void;
  onSamplerIdChange: (value: string | null) => void;
  period: SamplingPeriod;
  periodWindowHint?: string;
  samplerId: string | null;
  samplerOptions: { value: string; label: string }[];
  showPeriod?: boolean;
  visibleCheckbox?: {
    checked: boolean;
    onChange: (value: boolean) => void;
  };
}

const PeriodSamplerFields: FC<PeriodSamplerFieldsProps> = ({
  disabled = false,
  i18n,
  onPeriodChange,
  onSamplerIdChange,
  period,
  periodWindowHint,
  samplerId,
  samplerOptions,
  showPeriod = true,
  visibleCheckbox,
}) => {
  const periodOptions = buildPeriodSelectOptions(i18n);

  return (
    <Stack gap="xs">
      {showPeriod ? (
        <>
          <Select
            data={periodOptions}
            disabled={disabled}
            label={i18n('period')}
            onChange={(value) => onPeriodChange((value as SamplingPeriod) ?? 'spring')}
            size="sm"
            value={period}
          />
          {periodWindowHint ? (
            <Text c="dimmed" size="xs">
              {periodWindowHint}
            </Text>
          ) : null}
        </>
      ) : null}
      <Select
        data={samplerOptions}
        disabled={disabled}
        label={i18n('selectSampler')}
        onChange={onSamplerIdChange}
        searchable
        size="sm"
        value={samplerId}
      />
      {visibleCheckbox ? (
        <Checkbox
          checked={visibleCheckbox.checked}
          disabled={disabled}
          label={i18n('visibleToSampler')}
          onChange={(event) => visibleCheckbox.onChange(event.currentTarget.checked)}
          size="sm"
        />
      ) : null}
    </Stack>
  );
};

interface AssignmentMapActionPanelProps {
  capabilities: AssignmentMapCapabilities;
  clearAssignment: boolean;
  defaultMode: DrawActionMode;
  drawHint: string;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  isNgp: boolean;
  laboratoryId: string | null;
  laboratoryOptions: { value: string; label: string }[];
  mode: DrawActionMode;
  onClearAssignmentChange: (value: boolean) => void;
  onLaboratoryIdChange: (value: string | null) => void;
  onModeChange: (mode: DrawActionMode) => void;
  onPeriodChange: (period: SamplingPeriod) => void;
  onPrimaryAction: () => void;
  onReassignSamplerIdChange: (value: string | null) => void;
  onSamplerOrganizationIdChange: (value: string | null) => void;
  onSamplerUserIdChange: (value: string | null) => void;
  onVisibleToSamplerChange: (value: boolean) => void;
  period: SamplingPeriod;
  periodWindowHint?: string;
  preview: PreviewPolygonResponse | null;
  primaryActionDisabled: boolean;
  primaryActionLabel: string;
  primaryActionLoading: boolean;
  reassignSamplerId: string | null;
  samplerOrganizationId: string | null;
  samplerOrganizationOptions: { value: string; label: string }[];
  samplerOptions: { value: string; label: string }[];
  samplerUserId: string | null;
  visibleToSampler: boolean;
}

export const AssignmentMapActionPanel: FC<AssignmentMapActionPanelProps> = ({
  capabilities,
  clearAssignment,
  defaultMode,
  drawHint,
  i18n,
  isNgp,
  laboratoryId,
  laboratoryOptions,
  mode,
  onClearAssignmentChange,
  onLaboratoryIdChange,
  onModeChange,
  onPeriodChange,
  onPrimaryAction,
  onReassignSamplerIdChange,
  onSamplerOrganizationIdChange,
  onSamplerUserIdChange,
  onVisibleToSamplerChange,
  period,
  periodWindowHint,
  preview,
  primaryActionDisabled,
  primaryActionLabel,
  primaryActionLoading,
  reassignSamplerId,
  samplerOrganizationId,
  samplerOrganizationOptions,
  samplerOptions,
  samplerUserId,
  visibleToSampler,
}) => {
  const isTeamAssignment = isTeamAssignmentVariant(capabilities);
  const showModeSelector = capabilities.drawModes.length > 1;

  const gpModeOptions = capabilities.drawModes.map((value) => ({
    value,
    label:
      value === 'sms'
        ? i18n('modeSms')
        : value === 'sampler'
          ? i18n('modeSampler')
          : i18n('modeConfirm'),
  }));

  const ngpModeOptions = [
    {
      group: i18n('modeGroupMain'),
      items: capabilities.drawModes
        .filter((value) => value === 'sms' || value === 'sampler' || value === 'assign_laboratory')
        .map((value) => ({
          value,
          label:
            value === 'sms'
              ? i18n('modeSms')
              : value === 'sampler'
                ? i18n('modeSampler')
                : i18n('modeLaboratory'),
        })),
    },
    {
      group: i18n('modeGroupAdvanced'),
      items: capabilities.drawModes
        .filter(
          (value) =>
            value === 'make_visible' || value === 'reassign_sampler' || value === 'confirm_laboratory',
        )
        .map((value) => ({
          value,
          label:
            value === 'make_visible'
              ? i18n('modeMakeVisible')
              : value === 'reassign_sampler'
                ? i18n('modeReassign')
                : i18n('modeConfirm'),
        })),
    },
  ].filter((group) => group.items.length > 0);

  const renderSamplerFields = (
    samplerId: string | null,
    onSamplerIdChange: (value: string | null) => void,
    options?: { showPeriod?: boolean; showVisible?: boolean; disabled?: boolean },
  ) => {
    const showPeriod = options?.showPeriod ?? true;
    const fieldsDisabled = options?.disabled ?? false;

    if (capabilities.showSamplerOrganizationSelect) {
      return (
        <Stack gap="xs">
          <SamplerOrganizationFields
            disabled={fieldsDisabled}
            i18n={i18n}
            onOrganizationIdChange={onSamplerOrganizationIdChange}
            onSamplerIdChange={onSamplerIdChange}
            organizationId={samplerOrganizationId}
            organizationOptions={samplerOrganizationOptions}
            samplerId={samplerId}
            samplerOptions={samplerOptions}
          />
          {showPeriod ? (
            <>
              <Select
                data={buildPeriodSelectOptions(i18n)}
                disabled={fieldsDisabled}
                label={i18n('period')}
                onChange={(value) => onPeriodChange((value as SamplingPeriod) ?? 'spring')}
                size="sm"
                value={period}
              />
              {periodWindowHint ? (
                <Text c="dimmed" size="xs">
                  {periodWindowHint}
                </Text>
              ) : null}
            </>
          ) : null}
          {options?.showVisible ? (
            <Checkbox
              checked={visibleToSampler}
              disabled={fieldsDisabled}
              label={i18n('visibleToSampler')}
              onChange={(event) => onVisibleToSamplerChange(event.currentTarget.checked)}
              size="sm"
            />
          ) : null}
        </Stack>
      );
    }

    return (
      <PeriodSamplerFields
        disabled={fieldsDisabled}
        i18n={i18n}
        onPeriodChange={onPeriodChange}
        onSamplerIdChange={onSamplerIdChange}
        period={period}
        periodWindowHint={periodWindowHint}
        samplerId={samplerId}
        samplerOptions={samplerOptions}
        showPeriod={showPeriod}
        visibleCheckbox={
          options?.showVisible
            ? { checked: visibleToSampler, onChange: onVisibleToSamplerChange }
            : undefined
        }
      />
    );
  };

  return (
    <Paper
      bg="var(--mantine-color-gray-0)"
      p="md"
      radius="md"
      style={{ borderTop: '3px solid var(--mantine-color-pf-green-6)' }}
      withBorder
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={5}>
            {isTeamAssignment ? i18n('teamAssignmentActionTitle') : i18n('sidebarAction')}
          </Title>
          <Text c="dimmed" size="xs">
            {drawHint}
          </Text>
        </Stack>

        {showModeSelector ? (
          isNgp ? (
            <Select
              data={ngpModeOptions}
              label={i18n('actionMode')}
              onChange={(value) => onModeChange((value as DrawActionMode) ?? defaultMode)}
              value={mode}
            />
          ) : (
            <SegmentedControl
              color="pf-green"
              data={gpModeOptions}
              fullWidth
              onChange={(value) => onModeChange(value as DrawActionMode)}
              size="sm"
              value={mode}
            />
          )
        ) : null}

        {preview ? (
          <Alert
            color={preview.warnings.length ? 'orange' : 'blue'}
            p="sm"
            radius="sm"
            title={i18n('previewCount', { count: preview.count })}
            variant="light"
          >
            {preview.warnings.length ? (
              <Stack gap={4}>
                {preview.warnings.map((warning) => (
                  <Text key={warning} size="xs">
                    {warning}
                  </Text>
                ))}
              </Stack>
            ) : null}
          </Alert>
        ) : (
          <Paper bg="var(--mantine-color-body)" p="sm" radius="sm" withBorder>
            <Text c="dimmed" size="xs">
              {i18n('drawPolygonHint')}
            </Text>
          </Paper>
        )}

        {capabilities.allowClearAssignment &&
        (mode === 'sampler' || mode === 'assign_laboratory') ? (
          <Checkbox
            checked={clearAssignment}
            label={i18n('clearAssignment')}
            onChange={(event) => onClearAssignmentChange(event.currentTarget.checked)}
            size="sm"
          />
        ) : null}

        {mode === 'sampler'
          ? renderSamplerFields(samplerUserId, onSamplerUserIdChange, {
              showPeriod: true,
              showVisible: isNgp,
              disabled: clearAssignment,
            })
          : null}

        {mode === 'assign_laboratory' ? (
          <Select
            data={laboratoryOptions}
            disabled={clearAssignment}
            label={i18n('selectLaboratory')}
            onChange={onLaboratoryIdChange}
            searchable
            size="sm"
            value={laboratoryId}
          />
        ) : null}

        {mode === 'reassign_sampler'
          ? renderSamplerFields(reassignSamplerId, onReassignSamplerIdChange, {
              showPeriod: !isTeamAssignment,
              showVisible: false,
            })
          : null}

        {mode === 'confirm_laboratory' ? (
          <Select
            data={laboratoryOptions}
            label={i18n('selectLaboratory')}
            onChange={onLaboratoryIdChange}
            searchable
            size="sm"
            value={laboratoryId}
          />
        ) : null}

        <Button
          color="pf-green"
          disabled={primaryActionDisabled}
          fullWidth
          loading={primaryActionLoading}
          onClick={onPrimaryAction}
          size="sm"
        >
          {primaryActionLabel}
        </Button>
      </Stack>
    </Paper>
  );
};
